import { Button, Checkbox, Tabs } from '@any-ui-react/core';
import { useModals } from '@any-ui-react/modals';
import classNames from 'classnames';
import flatten from 'lodash-es/flatten';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { YearHalfRangeSelect } from '@/common/components/report/year-half-filter/YearHalfRangeSelect';
import type { HistoryListHandle } from '@/common/components/shared/export-import/components/HistoryList';
import { EXPORT_REQUEST_SENT_MESSAGE } from '@/common/components/shared/export-import/export-history/const';
import { ExportHistoryList } from '@/common/components/shared/export-import/export-history/ExportHistoryList';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { ContractTypeSelectMultiple } from '@/common/components/shared/select/contract-type/ContractTypeSelect';
import { toast } from '@/common/components/shared/toaster/toast';
import {
  type ContractType,
  HalfYear,
  NumberOfEmployeeExportType,
  useStartExportReportNumberOfEmployeeMutation,
} from '@/common/graphql';
import { useGoogleOfflineToken } from '@/common/hooks/general/useGoogleOfflineToken';
import type { FilterProps } from '@/common/hooks/shared/useFilters';
import type { NumberOfEmployeesFilterType } from '@/common/types';
import { ApiUtils, DateUtils } from '@/common/utils';

const EXPORT_TYPES: Record<string, NumberOfEmployeeExportType[]> = {
  General: [
    NumberOfEmployeeExportType.Summary,
    NumberOfEmployeeExportType.Company,
    NumberOfEmployeeExportType.Location,
    NumberOfEmployeeExportType.BusinessUnit,
    NumberOfEmployeeExportType.Job,
    NumberOfEmployeeExportType.CompanyBusinessUnitJob,
    NumberOfEmployeeExportType.CompanyBusinessUnitDepartment,
    NumberOfEmployeeExportType.Nationality,
    NumberOfEmployeeExportType.Turnover,
    NumberOfEmployeeExportType.ActiveEmployee,
  ],
  'Managerial Accounting': [
    NumberOfEmployeeExportType.Vdp,
    NumberOfEmployeeExportType.Vdd,
    NumberOfEmployeeExportType.VdpVddComposite,
    NumberOfEmployeeExportType.Functionality,
    NumberOfEmployeeExportType.RoleType,
    NumberOfEmployeeExportType.AllocationKey,
  ],
};
const ALL_EXPORT_TYPES = flatten(Object.values(EXPORT_TYPES));

export type ExportTypeCategory = 'General' | 'Managerial Accounting';

type ExportModalTab = 'export' | 'history';

const EXPORT_TYPE_LABELS: Record<NumberOfEmployeeExportType, string> = {
  [NumberOfEmployeeExportType.Summary]: 'Summary',
  [NumberOfEmployeeExportType.ActiveEmployee]: 'Active Employee',
  [NumberOfEmployeeExportType.Company]: 'Company',
  [NumberOfEmployeeExportType.Location]: 'Location',
  [NumberOfEmployeeExportType.BusinessUnit]: 'Business Unit',
  [NumberOfEmployeeExportType.Job]: 'Job',
  [NumberOfEmployeeExportType.CompanyBusinessUnitJob]: 'Company / Business Unit / Job',
  [NumberOfEmployeeExportType.CompanyBusinessUnitDepartment]: 'Company / Business Unit / Department',
  [NumberOfEmployeeExportType.Nationality]: 'Nationality',
  [NumberOfEmployeeExportType.Turnover]: 'Turnover',
  [NumberOfEmployeeExportType.Vdp]: 'VDP',
  [NumberOfEmployeeExportType.Vdd]: 'VDD',
  [NumberOfEmployeeExportType.VdpVddComposite]: 'VDP / VDD',
  [NumberOfEmployeeExportType.Functionality]: 'Functionality',
  [NumberOfEmployeeExportType.RoleType]: 'Role Type',
  [NumberOfEmployeeExportType.AllocationKey]: 'Allocation Key',
};

const yearMonthToYearHalf = (yearMonth: string): { year: number; half: HalfYear } => {
  const [yearStr, monthStr] = yearMonth.split('-');
  const month = Number(monthStr);
  return {
    year: Number(yearStr),
    half: month <= 6 ? HalfYear.H1 : HalfYear.H2,
  };
};

const startYear = DateUtils.dateAnyMindStarts.getFullYear();

export const yearHalfToStartYearMonth = (year: number, half: HalfYear): string =>
  half === HalfYear.H1 ? (year === startYear ? `${year}-04` : `${year}-01`) : `${year}-07`;

export const yearHalfToEndYearMonth = (year: number, half: HalfYear): string =>
  half === HalfYear.H1 ? `${year}-06` : `${year}-12`;

interface ContentProps {
  defaultCategory: ExportTypeCategory;
  filters: FilterProps<NumberOfEmployeesFilterType>;
  modalId: string;
  setActiveTab: (tab: ExportModalTab) => void;
}

const NumberOfEmployeesExportModalContent = ({ defaultCategory, filters, modalId, setActiveTab }: ContentProps) => {
  const { closeModal } = useModals();
  const { verifyToken } = useGoogleOfflineToken();

  const fromDefaults = useMemo(
    () => yearMonthToYearHalf(filters.current.startYearMonth),
    [filters.current.startYearMonth],
  );
  const toDefaults = useMemo(() => yearMonthToYearHalf(filters.current.endYearMonth), [filters.current.endYearMonth]);

  const [selectedTypes, setSelectedTypes] = useState<Set<NumberOfEmployeeExportType>>(
    new Set(EXPORT_TYPES[defaultCategory]),
  );
  const [fromYear, setFromYear] = useState(fromDefaults.year);
  const [fromHalf, setFromHalf] = useState<HalfYear>(fromDefaults.half);
  const [toYear, setToYear] = useState(toDefaults.year);
  const [toHalf, setToHalf] = useState<HalfYear>(toDefaults.half);
  const [contractTypes, setContractTypes] = useState<ContractType[]>(filters.current.contractTypes);
  const [loading, setLoading] = useState(false);

  const [startExportReportNumberOfEmployee] = useStartExportReportNumberOfEmployeeMutation({
    onError(error) {
      ApiUtils.handleApiError(error);
    },
    onCompleted() {
      toast.success(EXPORT_REQUEST_SENT_MESSAGE);
      setActiveTab('history');
    },
  });

  const toggleType = useCallback((type: NumberOfEmployeeExportType) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedTypes(prev => (prev.size === ALL_EXPORT_TYPES.length ? new Set() : new Set(ALL_EXPORT_TYPES)));
  }, []);

  const onExport = async () => {
    setLoading(true);
    await verifyToken(import.meta.env.VITE_REPORT_CALLBACK_URL);
    await startExportReportNumberOfEmployee({
      variables: {
        startYearMonth: yearHalfToStartYearMonth(fromYear, fromHalf),
        endYearMonth: yearHalfToEndYearMonth(toYear, toHalf),
        contractTypes,
        exportTypes: Array.from(selectedTypes),
      },
    });
    setLoading(false);
  };

  const allChecked = selectedTypes.size === ALL_EXPORT_TYPES.length;
  const indeterminate = selectedTypes.size > 0 && selectedTypes.size < ALL_EXPORT_TYPES.length;
  const isGeneralSelected = useMemo(() => EXPORT_TYPES.General.some(type => selectedTypes.has(type)), [selectedTypes]);

  return (
    <div className="flex flex-col gap-y-4">
      {/* Export type checkboxes */}
      <div>
        <div className="mb-2 text-sm">Export Sheets</div>
        <div className="mb-2">
          <Checkbox checked={allChecked} indeterminate={indeterminate} label="Select All" onChange={toggleAll} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(EXPORT_TYPES).map(([genre, types]) => (
            <div key={genre}>
              <div className="mb-2 text-gray-7 uppercase">{genre}</div>
              {types.map((type, index) => (
                <Checkbox
                  className={classNames({ 'mt-1': index > 0 })}
                  key={type}
                  checked={selectedTypes.has(type)}
                  label={EXPORT_TYPE_LABELS[type]}
                  onChange={() => toggleType(type)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Period selectors */}
      <div>
        <p className="mb-1 text-sm">Period</p>
        <YearHalfRangeSelect
          fromHalf={fromHalf}
          fromYear={String(fromYear)}
          toHalf={toHalf}
          toYear={String(toYear)}
          onChangeFromHalf={setFromHalf}
          onChangeFromYear={v => setFromYear(Number(v))}
          onChangeToHalf={setToHalf}
          onChangeToYear={v => setToYear(Number(v))}
        />
      </div>

      {/* Contract types */}
      <div>
        <p className="mb-1 text-sm">Contract Types</p>
        <ContractTypeSelectMultiple
          disabled={!isGeneralSelected}
          value={contractTypes}
          onChange={items => setContractTypes(items.map(({ value }) => value))}
        />
        {!isGeneralSelected && (
          <p className="mt-1 text-gray-6 text-xs">Not applicable - no "General" sheets are selected.</p>
        )}
      </div>

      <ModalFooter spacerSize={-1}>
        <Button variant="default" onClick={() => closeModal(modalId)}>
          Close
        </Button>
        <Button disabled={selectedTypes.size === 0} loading={loading} type="submit" onClick={onExport}>
          Export
        </Button>
      </ModalFooter>
    </div>
  );
};

interface Props {
  defaultCategory: ExportTypeCategory;
  filters: FilterProps<NumberOfEmployeesFilterType>;
  opened: boolean;
  onClose: () => void;
}

export const NumberOfEmployeesExportModal = ({ defaultCategory, filters, opened, onClose }: Props) => {
  const { openModal, closeModal } = useModals();
  const modalId = 'number-of-employees-export-modal';
  const [activeTab, setActiveTab] = useState<ExportModalTab>('export');
  const wasOpenedRef = useRef(false);
  const historyListRef = useRef<HistoryListHandle>(null);

  useEffect(() => {
    if (activeTab === 'history') {
      historyListRef.current?.refetch();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!opened) {
      wasOpenedRef.current = false;
      closeModal(modalId);
      return;
    }

    const tab = wasOpenedRef.current ? activeTab : 'export';
    wasOpenedRef.current = true;
    if (tab !== activeTab) {
      setActiveTab(tab);
    }

    openModal({
      modalId,
      closeOnClickOutside: false,
      closeOnEscape: true,
      withinPortal: false,
      centered: false,
      title: 'Export Number of Employees',
      size: '1024px',
      onClose,
      children: (
        <Tabs
          className="-mx-6 -mt-4"
          keepMounted={false}
          value={tab}
          onChange={value => setActiveTab(value === 'history' ? 'history' : 'export')}
        >
          <Tabs.List className="px-6">
            <Tabs.Tab value="export">Export</Tabs.Tab>
            <Tabs.Tab value="history">History</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel className="px-6 pt-4" value="export">
            <NumberOfEmployeesExportModalContent
              defaultCategory={defaultCategory}
              filters={filters}
              modalId={modalId}
              setActiveTab={setActiveTab}
            />
          </Tabs.Panel>
          <Tabs.Panel value="history">
            <ExportHistoryList ref={historyListRef} kind="numberOfEmployee" modalId={modalId} />
          </Tabs.Panel>
        </Tabs>
      ),
    });
  }, [opened, openModal, closeModal, filters, onClose, defaultCategory, activeTab]);

  return null;
};
